import React from 'react';
import styled from 'styled-components';
import { openChat } from 'packages/smart-home-support/utils';
import { DynamicLink, DynamicLinkProps, ShowForTestGroup } from 'packages/smart-home-support/components';
import { usePartner, partnerValue } from 'packages/smart-home-support/partner';
import * as icons from './icons';
import { ProfilePageProps } from '../../models';
import BREAK_POINTS from 'packages/smart-home-support/styling/media-queries';
import { ExpertPortrait, TabContent, Well } from '../../styles';
import { PartnerContent } from 'packages/smart-home-support/partner';
import { getExpertSessionsUrl, getPhoneUrl } from '../../utils';
import { LinkId } from 'packages/smart-home-support/constants';
import { default as ContentCards } from './ContentCards';
import msgIcon from './resources/message-square.svg';

const s = {
  Heading: styled.h1`
    font-weight: bold;
    font-size: 40px;
    margin-top: 0px;
    margin-bottom: 15px;
    @media ${BREAK_POINTS.tablet} {
      font-size: 40px;
    }
    @media ${BREAK_POINTS.desktop} {
      font-size: 50px;
    }
  `,
  HeadingWrapper: styled.div`
    font-size: 18px;
    width: 100%;
  `,
  ContentPanel: styled.div`
    @media ${BREAK_POINTS.tablet} {
      display: flex;
      flex-direction: row;
    }
  `,
  LeftPanel: styled.div`
    padding-right: 10px;
    @media ${BREAK_POINTS.tablet} {
      width: 70%;
    }
    @media ${BREAK_POINTS.desktopBig} {
      width: 65%;
    }
  `,
  RightPanel: styled.div`
    padding-left: 10px;
    @media ${BREAK_POINTS.tablet} {
      width: 30%;
    }
    @media ${BREAK_POINTS.desktopBig} {
      width: 35%;
    }
  `,
  Heading2: styled.h2`
    font-size: 24px;
    font-weight: bold;
  `,
  WellSubheading: styled.p`
    margin-top: 7px;
    margin-bottom: 0px;
    font-size: 18px;
    font-weight: normal;
    font-style: normal;
  `,
  TabContent: styled(TabContent)`
    display: flex;
    flex-direction: column;
    @media ${BREAK_POINTS.tablet} {
      display: flex;
    }
  `,
  Well: styled(Well)`
    margin-top: 16px;
    margin-bottom: 15px;
  `,
  ExpertPortrait: styled(ExpertPortrait)`
    margin-right: 10px;
    width: 100px;
    height: 70px;
    @media ${BREAK_POINTS.tablet} {
      width: 80px;
      height: 56px;
    }
    @media ${BREAK_POINTS.desktopBig} {
      width: 100px;
      height: 70px;
    }
  `,
  WellTop: styled.div`
    display: flex;
    justify-content: space-between;
    @media ${BREAK_POINTS.tablet} {
      margin-bottom: 50px;
    }
  `,
  ActionGrid: styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: auto;
    grid-gap: 2em 1.5em;
    text-align: center;
    padding: 0;
    margin-top: 1.5em;

    @media ${BREAK_POINTS.tablet} {
      grid-template-columns: repeat(5, 1fr);
    }
  `,
  Action: styled(DynamicLink)`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 25px;
    color: #000;
    text-decoration: none;
    }
  `,
  ActionIcon: styled.span`
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    background-color: #f6f6f6;
    width: 64px;
    height: 64px;
    border-radius: 9999px;
    padding: 20px;
    transition: background-color 0.2s;
    &:hover {
      background-color: #f1f1f1;
    }
    @media ${BREAK_POINTS.tablet} {
      width: 72px;
      height: 72px;
    }
  `,
  ActionLabel: styled.span`
    margin-top: 12px;
    margin-bottom: 0;
    font-size: 14px;
    font-weight: 400;
    font-style: normal;
    text-align: center;
  `,
  WellActionContainer: styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 68px;
    box-sizing: border-box;
    border: 1px solid #d8dada;
    border-radius: 8px;
    background: #fff;
    margin: 16px 0;
    transition: border-color 0.2s, background-color 0.2s;
    &:hover {
      border-color: #a8aaaa;
    }
  `,
  WellAction: styled(DynamicLink)`
    padding: 15px 24px;
    font-size: 16px;
    text-align: left;
    text-decoration: none;
    color: #000;
  `,
  MessageIcon: styled.img`
    float: left;
    height: 28px;
    width: 28px;
  `,
};

type ActionProps = DynamicLinkProps & {
  icon: React.ComponentType;
  label: string;
};

const Action: React.FC<ActionProps> = (props) => {
  return (
    <s.Action id={props.id} onClick={props.onClick} to={props.to} target={props.target}>
      <s.ActionIcon>
        <props.icon />
      </s.ActionIcon>
      <s.ActionLabel>{props.label}</s.ActionLabel>
    </s.Action>
  );
};

const HomeTabActivation: React.FC<ProfilePageProps> = () => {
  const [{ claimUrl }] = usePartner();

  return (
    <s.TabContent>
      <s.HeadingWrapper>
        <s.Heading>Welcome Home!</s.Heading>
        We protect your home&rsquo;s connected devices. All under one plan.
      </s.HeadingWrapper>
      <s.ContentPanel>
        <s.LeftPanel>
          <ContentCards />
          <s.ActionGrid>
            <Action
              id={LinkId.DashboardHomeActionChat}
              label={partnerValue<string>({ Sprint: 'Live chat' }, 'Chat now')}
              icon={icons.Chat}
              onClick={openChat}
            />
            <Action id={LinkId.DashboardHomeActionCall} label="Call now" icon={icons.Call} to={getPhoneUrl()} />
            <Action
              id={LinkId.DashboardHomeActionClaim}
              label="Start a claim"
              icon={icons.Claim}
              to={claimUrl}
              target="_blank"
            />
            <Action
              id={LinkId.DashboardHomeActionWhatsCovered}
              label="What's covered?"
              icon={icons.WhatsCovered}
              to="/whats-covered"
            />
            <PartnerContent TechCoachHome={null}>
              <Action id={LinkId.DashboardHomeActionFaq} label="FAQ" icon={icons.FAQ} to="/faq" />
            </PartnerContent>
          </s.ActionGrid>
        </s.LeftPanel>
        <s.RightPanel>
          <s.Well>
            <s.WellTop>
              <div>
                <s.Heading2>Have questions?</s.Heading2>
                <s.WellSubheading>
                  <PartnerContent Sprint="Tech Experts have your back">
                    Tech Coaches are here for you 24/7
                  </PartnerContent>
                </s.WellSubheading>
              </div>
              <s.ExpertPortrait />
            </s.WellTop>
            <s.WellActionContainer>
              <s.WellAction id={LinkId.DashboardHomeSupportChat} onClick={openChat}>
                <s.MessageIcon src={msgIcon} />
                &nbsp;I have a tech question and could use some help.
              </s.WellAction>
            </s.WellActionContainer>
            <s.WellActionContainer>
              <s.WellAction id={LinkId.DashboardHomeSupportSupport} onClick={openChat}>
                <s.MessageIcon src={msgIcon} />
                &nbsp;One of my devices isn&rsquo;t working, and I need to get it fixed.
              </s.WellAction>
            </s.WellActionContainer>
            <s.WellActionContainer>
              <s.WellAction id={LinkId.DashboardHomeSupportAdvice} onClick={openChat}>
                <s.MessageIcon src={msgIcon} />
                &nbsp;I need some personalized tech advice.
              </s.WellAction>
            </s.WellActionContainer>
            <PartnerContent TechCoachHome="" Asurion="">
              <s.WellActionContainer>
                <s.WellAction
                  id={LinkId.DashboardHomeSupportExpertSessions}
                  to={`${getExpertSessionsUrl()}?megaUseCase=securityprivacy`}
                  target="_blank"
                  showLeavingIcon
                >
                  <s.MessageIcon src={msgIcon} />
                  &nbsp;I want to schedule a 1:1 data privacy review.
                </s.WellAction>
              </s.WellActionContainer>
            </PartnerContent>
          </s.Well>
        </s.RightPanel>
      </s.ContentPanel>
    </s.TabContent>
  );
};

export default HomeTabActivation;



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/profile/tabs/HomeTab/HomeTabActivation.tsx